import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useGamification } from '../../contexts/GamificationContext';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import CandidateStats from '../../components/admin/CandidateStats';
import PointsDisplay from '../../components/gamification/PointsDisplay';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiBriefcase, FiCalendar, FiTrendingUp, FiClock, FiCheck, FiX, FiDownload, FiBarChart2 } = FiIcons;

const HostDashboard = () => {
  const { events, companies, jobs, interviewRequests, interviews, candidates } = useData();
  const { addPoints } = useGamification();
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'candidates'

  const stats = {
    totalCandidates: candidates.length,
    totalCompanies: companies.length,
    totalJobs: jobs.length,
    totalRequests: interviewRequests.length,
    confirmedInterviews: interviews.length,
    completedInterviews: interviews.filter(i => i.status === 'completed').length,
    pendingRequests: interviewRequests.filter(req => req.status === 'pending').length,
    acceptedRequests: interviewRequests.filter(req => req.status === 'accepted').length
  };

  const handleExportData = (type) => {
    console.log(`Exporting ${type} data...`);
    alert(`${type} data exported successfully!`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Host Dashboard</h1>
        <p className="text-gray-600">Manage your job fair event, invite companies, and track overall performance.</p>
      </motion.div>

      <div className="mb-8">
        <PointsDisplay />
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiTrendingUp} />
              <span>Overview</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('candidates')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'candidates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiBarChart2} />
              <span>Candidate Analytics</span>
            </div>
          </button>
        </nav>
      </div>

      {activeTab === 'candidates' ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <CandidateStats />
        </motion.div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.1 }} 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <SafeIcon icon={FiUsers} className="text-blue-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Registered Candidates</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalCandidates}</p>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.2 }} 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-lg">
                  <SafeIcon icon={FiBriefcase} className="text-green-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Companies Invited</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalCompanies}</p>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.3 }} 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <SafeIcon icon={FiCalendar} className="text-purple-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Interview Requests</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalRequests}</p>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.4 }} 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <SafeIcon icon={FiTrendingUp} className="text-orange-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Confirmed Interviews</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.confirmedInterviews}</p>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Interview Status Overview */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.5 }} 
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Interview Status Overview</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="bg-yellow-100 p-2 rounded-lg">
                      <SafeIcon icon={FiClock} className="text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Pending Requests</p>
                      <p className="text-sm text-gray-600">Awaiting company response</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-yellow-600">{stats.pendingRequests}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <SafeIcon icon={FiCheck} className="text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Accepted Requests</p>
                      <p className="text-sm text-gray-600">Interviews scheduled</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-green-600">{stats.acceptedRequests}</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <SafeIcon icon={FiTrendingUp} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Completed Interviews</p>
                      <p className="text-sm text-gray-600">Via QR code scanning</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">{stats.completedInterviews}</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.6 }} 
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
              <div className="space-y-4">
                <Link 
                  to="/host/events" 
                  className="block w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors text-center font-medium"
                >
                  Manage Events
                </Link>
                <Link 
                  to="/host/companies" 
                  className="block w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors text-center font-medium"
                >
                  Invite & Manage Companies
                </Link>
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    onClick={() => handleExportData('Candidates')}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium inline-flex items-center justify-center space-x-1"
                  >
                    <SafeIcon icon={FiDownload} />
                    <span>Export Candidates</span>
                  </button>
                  <button 
                    onClick={() => handleExportData('Interviews')}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium inline-flex items-center justify-center space-x-1"
                  >
                    <SafeIcon icon={FiDownload} />
                    <span>Export Interviews</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </div>
  );
};

export default HostDashboard;